import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from "@headlessui/react";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import { Line, useHtmlParser } from "@sorcery/ui";

import "react-quill/dist/quill.snow.css";

import { useAtomValue } from "jotai";

import { Button, P, toast } from "@sorcery/ui";

import { deckJotai } from "~/state/deckJotai";
import { annotatePrimerMarkup, getPrimerParserOptions } from "~/utils/html";
import { api } from "~/utils/trpc";

const ReactQuill = dynamic(() => import("react-quill"), { ssr: false });

export const EditPrimer = () => {
  const trpcUtils = api.useUtils();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  const [previewing, setPreviewing] = useState(false);
  const [primerText, setPrimerText] = useState(deck.primer ?? "");
  const { mapToReactComponents } = useHtmlParser();
  const [error, setError] = useState("");

  const { mutateAsync: updatePrimer, error: setPrimerError } = api.deck.updatePrimer.useMutation({
    onSuccess: () => {
      toast("Primer updated");
      void trpcUtils.deck.getById.invalidate({ id: deck.id });
    },
  });

  // Track any trpc errors for display
  useEffect(() => {
    if (setPrimerError) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [setPrimerError]);

  const handleChange = (content: string) => {
    setPrimerText(content);
  };

  const onSubmit = () => {
    // clear errs
    setError("");
    void updatePrimer({ deckId: deck.id, primer: primerText });
  };

  return (
    <>
      <Button
        variant={"secondary"}
        className="mb-4 mt-1"
        onClick={() => setPreviewing(!previewing)}
      >
        {previewing ? "Edit Primer" : "Preview Primer"}
      </Button>
      {previewing ? (
        <div className="curiosa-primer">
          {mapToReactComponents(annotatePrimerMarkup(primerText), getPrimerParserOptions())}
        </div>
      ) : (
        <>
          <div className="curiosa-quote">
            <P>
              Thanks for opting to create a primer. It can be helpful for both new and veteran
              players, and will cause your deck to be featured with a prominent "primer" badge on
              curiosa.io.
            </P>

            <Disclosure>
              {({ open }) => (
                <>
                  <DisclosureButton
                    className={"flex flex-row items-center justify-between text-left"}
                  >
                    <P className="!mb-2 italic underline">
                      <span>{open ? "Hide" : "Show"}</span>
                      <span>&nbsp;primer guide</span>
                    </P>
                  </DisclosureButton>

                  <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <DisclosurePanel>
                      <P className="pt-4 font-semibold">Content</P>
                      <P>
                        Here are some ideas on what to cover. Don't stress if you can't cover it
                        all, a little is better than nothing. If you want to see how your primer
                        will look to other people, click the "preview" button above.
                      </P>
                      <ul className="mb-6 ml-8 list-outside list-disc">
                        <li>Overall theme (what is the deck trying to do?)</li>
                        <li>Strengths and weaknesses.</li>
                        <li>
                          Strategy (how does it win the game, what's plan A, what's plan B, how do
                          you plan to get there?)
                        </li>
                        <li>
                          Opening hand / mulligans (key cards to hold and cards you definitely want
                          to mulligan, and why)
                        </li>
                        <li>
                          Single card discussion (are there any standout cards / interesting
                          interactions you want to highlight?)
                        </li>
                        <li>Matchup dependant advice</li>
                        <li>Sideboard cards (when to sub them in, and why)</li>
                      </ul>
                      <P className="font-semibold">Syntax</P>
                      <ul className="mb-6 ml-8 list-outside list-disc">
                        <li>Card annotations: double square brackets e.g. [[firebolts]]</li>
                        <li>
                          Youtube: double squiggly brackets e.g.{" "}
                          {"{{https://www.youtube.com/watch?v=d9YM_9CVmtc}}"}
                        </li>
                      </ul>
                    </DisclosurePanel>
                  </Transition>
                </>
              )}
            </Disclosure>
          </div>
          <div className="mt-2.5">
            <ReactQuill
              theme="snow"
              defaultValue={primerText}
              onChange={handleChange}
              placeholder={"Your primer..."}
            />
          </div>

          <Line className="my-4" />

          <Button className="mb-4" onClick={onSubmit}>
            Save changes
          </Button>
          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};
